<template>
  <div class="guest-nav-bar absolute w-full top-0 z-[10001] bg-white">
    <div
      class="max-w-[1280px] mx-auto h-16 flex items-center custom-padding justify-between relative"
    >
      <div class="flex gap-12">
        <div class="flex gap-3 items-center">
          <router-link to="/home" class="flex items-center gap-2">
            <div class="logo boer overflow-hidden h-[35px]">
              <PtIcon :icon="Logo" class="!h-[35px] !w-[115px]" />
            </div>
          </router-link>
          <div class="logo-extras">
            <div class="bg-grey-40 h-6 w-[1px]"></div>
          </div>
          <div class="bg-grey-30 px-2 py-[3px] rounded logo-extras">
            {{ t(`home.products.mse`) }}
          </div>
        </div>

        <div class="flex gap-2 items-center responsive-menu">
          <button
            class="rounded px-3 py-2 hover:bg-[#B3BAC533] bookmarks"
            @click="scrollIntoSection('toolkit')"
          >
            {{ t('NavBar.features') }}
          </button>
          <button
            class="rounded px-3 py-2 hover:bg-[#B3BAC533] bookmarks"
            @click="scrollIntoSection('pricing')"
          >
            {{ t('NavBar.pricing') }}
          </button>
        </div>
      </div>

      <div class="flex gap-2 items-center min-w-0">
        <div class="bookmark-menu">
          <button
            class="w-10 h-10 flex items-center justify-center bg-grey-20 hover:bg-grey-30 rounded"
            @click="toggleBookmarkMenu"
          >
            <PtIcon icon="SolidMenu3" class="!w-6 !h-6 hover:fill-gray-90 fill-[#BCC2CC]" />
          </button>

          <div
            class="absolute top-full left-0 bg-grey-10 gap-2.5 right-0 flex flex-col p-5"
            v-if="ifBookmarkMenu"
          >
            <button
              class="py-3 px-2.5"
              @click="
                () => {
                  scrollIntoSection('toolkit');
                  toggleBookmarkMenu();
                }
              "
            >
              {{ t('NavBar.features') }}
            </button>
            <button
              class="py-3 px-2.5"
              @click="
                () => {
                  scrollIntoSection('pricing');
                  toggleBookmarkMenu();
                }
              "
            >
              {{ t('NavBar.pricing') }}
            </button>

            <div class="w-full h-[1px] bg-grey-40 bookmark-extras"></div>
            <button
              class="border-grey-40 bookmark-extras border rounded py-3 px-6"
              @click="toggleSmallLanguageMenu"
            >
              <div>{{ currentLanguage }}</div>
            </button>

            <div
              v-if="ifSmallLanguageMenu"
              class="fixed inset-0 bg-black/20 flex items-center justify-center"
              @click="toggleSmallLanguageMenu"
            >
              <div class="bg-white rounded p-4 flex flex-col gap-4 w-[80%]">
                <button
                  v-for="l of [
                    {
                      label: 'English',
                      key: 'en',
                    },
                    {
                      label: '简体中文',
                      key: 'cn',
                    },
                    {
                      label: '繁體中文',
                      key: 'tw',
                    },
                    {
                      label: '日本語',
                      key: 'jp',
                    },
                  ]"
                  @click="changeLanguage(l.key)"
                  :class="[
                    'w-full px-2 py-1 hover:bg-gray-50 flex items-center justify-between rounded',
                    i18n.global.locale.value === l.key ? 'text-eureka-500' : 'text-gray-700',
                  ]"
                >
                  <span class="text-sm">{{ l.label }}</span>
                  <PtIcon
                    icon="SolidTick"
                    class="!w-6 !h-6 text-eureka-500 fill-eureka-500"
                    v-if="i18n.global.locale.value === l.key"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="relative language-menu">
          <button
            @click.stop="showLanguageDropdown = !showLanguageDropdown"
            class="flex gap-1 hover:bg-[#B3BAC533] rounded items-center px-3 py-2"
          >
            <span class="text-sm">{{ currentLanguage }}</span>
            <PtIcon icon="SolidArrowsmallDown" class="!w-6 !h-6 text-gray-600 fill-gray-600" />
          </button>

          <!-- Language Dropdown -->
          <div
            v-if="showLanguageDropdown"
            class="absolute right-0 mt-2 w-[180px] bg-white rounded border border-gray-40 p-2 z-50 gap-1 flex flex-col"
            @click.stop
          >
            <button
              v-for="l of [
                {
                  label: 'English',
                  key: 'en',
                },
                {
                  label: '简体中文',
                  key: 'cn',
                },
                {
                  label: '繁體中文',
                  key: 'tw',
                },
                {
                  label: '日本語',
                  key: 'jp',
                },
              ]"
              @click="changeLanguage(l.key)"
              :class="[
                'w-full px-2 py-1 hover:bg-gray-50 flex items-center justify-between rounded',
                i18n.global.locale.value === l.key ? 'text-eureka-500' : 'text-gray-700',
              ]"
            >
              <span class="text-sm">{{ l.label }}</span>
              <PtIcon
                icon="SolidTick"
                class="!w-6 !h-6 text-eureka-500 fill-eureka-500"
                v-if="i18n.global.locale.value === l.key"
              />
            </button>
          </div>
        </div>

        <button
          @click="handleGetStarted"
          class="flex min-w-0 items-center gap-2 px-6 py-2.5 bg-eureka-600 hover:bg-eureka-700 text-white rounded-lg transition-colors font-medium"
        >
          <PtIcon icon="SolidRocket" class="!w-5 !h-5 fill-white shrink-0" />
          <span class="truncate">{{ t('NavBar.getStarted') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { PtIcon } from 'patsnap-biz';
import { type HeaderService } from '@patsnap-utils/biz';
import { loginSDK } from '@eureka/shared/login-sdk';
import Logo from '../assets/eureka-logo-color.svg';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/infrastructure/locales';
import { cn, en, jp, tw } from '../infrastructure/locales';
import '@patsnap-ui/icon/assets/solid/Menu3.svg';

// Merge nav-bar translations into global i18n
i18n.global.mergeLocaleMessage('cn', cn);
i18n.global.mergeLocaleMessage('en', en);
i18n.global.mergeLocaleMessage('jp', jp);
i18n.global.mergeLocaleMessage('tw', tw);

const { t } = useI18n();

const props = defineProps<{
  headerService: HeaderService;
  login?: () => void;
}>();

const showLanguageDropdown = ref(false);
const showMobileMenu = ref(false);

const languageMap = {
  en: 'English',
  cn: '简体中文',
  tw: '繁體中文',
  jp: '日本語',
};

const currentLanguage = computed(() => {
  // Use i18n.global.locale to make it reactive
  const lang = i18n.global.locale.value;
  return languageMap[lang as keyof typeof languageMap] || 'Language';
});

function handleGetStarted() {
  if (props.login) {
    props.login();
    return;
  }
  loginSDK.open({ silence: true }).then(() => {
    window.location.reload();
  });
}

function changeLanguage(lang: string) {
  showLanguageDropdown.value = false;

  // Update i18n locale without page reload
  i18n.global.locale.value = lang as 'en' | 'cn' | 'jp' | 'tw';

  // Set cookie to persist language preference
  // document.cookie = `---uniform-setting-locale=${lang}; path=/; max-age=31536000`;

  // Call headerService if available (for any additional side effects)
  if (props.headerService.beforeLocaleChange) {
    props.headerService.beforeLocaleChange(lang, () => {
      window.location.reload();
    });
  }
}

const scrollIntoSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
  });
};

const ifBookmarkMenu = ref(false);

const toggleBookmarkMenu = () => {
  ifBookmarkMenu.value = !ifBookmarkMenu.value;
};

const ifSmallLanguageMenu = ref(false);

const toggleSmallLanguageMenu = () => {
  ifSmallLanguageMenu.value = !ifSmallLanguageMenu.value;
};

onMounted(() => {
  // Close dropdowns when clicking outside
  document.addEventListener('click', () => {
    showLanguageDropdown.value = false;
  });
});
</script>

<style lang="scss" scoped>
.guest-nav-bar {
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.custom-padding {
  @apply px-20;
}

.responsive-menu {
  display: flex;
}

.logo {
  width: 115px;
}

.logo-extras {
  display: block;
}

.language-menu {
  display: block;
}

.bookmark-menu {
  display: none;
}

@media (max-width: 768px) {
  .custom-padding {
    @apply px-10;
  }

  .responsive-menu {
    display: none;
  }

  .bookmark-menu {
    display: block;

    .bookmark-extras {
      display: none;
    }
  }
}

@media (max-width: 375px) {
  .custom-padding {
    @apply px-5;
  }

  .logo {
    width: 53px;
  }

  .logo-extras,
  .language-menu {
    display: none;
  }

  .bookmark-menu {
    display: block;

    .bookmark-extras {
      display: block;
    }
  }
}
</style>
